import dayjs, { Dayjs } from 'dayjs';
import { MIN_ACCEPTED_DATE } from '../constants/app';
import { ILabel } from '../constants/interfaces';

export const getMinAcceptedDate = (): Date => {
  const minDate = new Date(MIN_ACCEPTED_DATE);
  minDate.setHours(0, 0, 0, 0);
  return minDate;
};

export const isBeforeMinAcceptedDate = (value: string): boolean => {
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return false;
  }
  return isDayjsBeforeMinAcceptedDate(parsed);
};

export const isInvalidDateLabelResponse = (
  value: string | undefined
): boolean => {
  if (!value || value.trim() === '') {
    return false;
  }
  const parsed = dayjs(value);
  return !parsed.isValid() || isDayjsBeforeMinAcceptedDate(parsed);
};

export const getDateLabelResponseError = (
  value: string | undefined
): string | undefined => {
  if (!value || value.trim() === '') {
    return undefined;
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return 'Invalid date';
  }
  if (isDayjsBeforeMinAcceptedDate(parsed)) {
    return minAcceptedDateErrorMessage();
  }
  return undefined;
};

export const minAcceptedDateErrorMessage = (): string =>
  `Date cannot be before ${getMinAcceptedDate().toLocaleDateString()}`;

export const getMinAcceptedDayjs = (): Dayjs =>
  dayjs(MIN_ACCEPTED_DATE).startOf('day');

export const isDayjsBeforeMinAcceptedDate = (date: Dayjs): boolean =>
  date.isValid() && date.isBefore(getMinAcceptedDayjs());

export const isDateLabelType = (type: string | undefined): boolean => {
  const normalizedType = type?.toLowerCase();
  return normalizedType === 'date' || normalizedType === 'datetime';
};

export const isEmptyLabelResponse = (value: string | undefined): boolean =>
  !value || value.trim() === '';

export const formatLocaleDateOrNotSpecified = (date?: string): string =>
  date ? new Date(date).toLocaleDateString() : 'Not specified';

export const validateWorkflowLabelResponses = (
  labels: ILabel[],
  labelResponses: Record<number, string>
): Record<number, string> => {
  const errors: Record<number, string> = {};

  labels.forEach((label) => {
    const response = labelResponses[label.labelId];

    if (label.isMandatory && isEmptyLabelResponse(response)) {
      errors[label.labelId] = `${label.text} is required`;
    }

    if (isDateLabelType(label.type) && response) {
      const dateError = getDateLabelResponseError(response);
      if (dateError) {
        errors[label.labelId] = dateError;
      }
    }
  });

  return errors;
};

export const getMissingMandatoryLabelTexts = (
  labels: ILabel[],
  labelResponses: Record<number, string>
): string[] =>
  labels
    .filter(
      (label) =>
        label.isMandatory && isEmptyLabelResponse(labelResponses[label.labelId])
    )
    .map((label) => label.text);

export const areWorkflowLabelResponsesValid = (
  labels: ILabel[],
  labelResponses: Record<number, string>
): boolean =>
  labels.every((label) => {
    const response = labelResponses[label.labelId];

    if (label.isMandatory && isEmptyLabelResponse(response)) {
      return false;
    }

    if (isDateLabelType(label.type) && isInvalidDateLabelResponse(response)) {
      return false;
    }

    return true;
  });

// Extend the global window interface for summation validation
declare global {
  interface Window {
    summationValidation?: Record<number, boolean>;
    summationValidationTriggered?: boolean;
  }
}

export const fieldValidation = (
  type: string,
  value: string,
  isRequired: boolean
): string | undefined => {
  let errorText: string | undefined = undefined;

  // istanbul ignore next
  const trimmedValue = typeof value === 'string' ? value.trim() : value;

  if (isRequired && !trimmedValue) {
    errorText = 'Required';
  }
  return errorText;
};

export const fieldValidations = (
  fields: any[],
  values: any
): Record<string, string> => {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const { type, isMandatory, labelId } = field;
    const valueObj = values[labelId];
    const value = valueObj?.value ?? '';
    const errorText = fieldValidation(type, value, isMandatory);
    if (errorText && !field.type.includes('|column')) {
      errors[labelId] = errorText;
    }

    // Check for date field rule violations
    if (isDateLabelType(type) && value) {
      const dateError = getDateLabelResponseError(value);
      if (dateError) {
        errors[labelId] = dateError;
      } else if (field.rules) {
        try {
          const rules = JSON.parse(field.rules);
          if (rules.date) {
            const dateValue = new Date(value);

            // Compare dates at start of day to avoid time issues
            dateValue.setHours(0, 0, 0, 0);

            // Check if date is valid before comparing
            if (!isNaN(dateValue.getTime())) {
              if (rules.date.min) {
                const minDate = new Date(rules.date.min);
                minDate.setHours(0, 0, 0, 0);
                if (!isNaN(minDate.getTime()) && dateValue < minDate) {
                  errors[labelId] =
                    `Date cannot be before ${minDate.toLocaleDateString()}`;
                }
              }
              if (rules.date.max) {
                const maxDate = new Date(rules.date.max);
                maxDate.setHours(0, 0, 0, 0);
                if (!isNaN(maxDate.getTime()) && dateValue > maxDate) {
                  errors[labelId] =
                    `Date cannot be after ${maxDate.toLocaleDateString()}`;
                }
              }
            }
          }
        } catch (error) {
          console.warn(
            `Failed to parse date field rules for field ${labelId}:`,
            error
          );
        }
      }
    }

    // Check for summation field validation errors
    if (type === 'summation' && typeof window !== 'undefined') {
      // Trigger validation check in summation components
      window.summationValidationTriggered = true;

      const summationValidation = window.summationValidation;
      if (summationValidation && summationValidation[labelId]) {
        errors[labelId] = 'Please complete all required fields in this section';
      }

      // Also check if the total value is 0 directly
      const totalValue = parseFloat(value) || 0;
      if (totalValue === 0) {
        errors[labelId] = 'Total amount cannot be zero';
      }
    }
  }
  return errors;
};
